import {
  Component,
  DoCheck,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from "@angular/core";
import { UntypedFormGroup, UntypedFormBuilder, Validators } from "@angular/forms";
import { UserService } from "@career/core/services/user.service";
import { FieldConfig } from "@career/pocket-forms/models/field";
import { Subscription } from "rxjs";

@Component({
  exportAs: "dynamicForm",
  selector: "dynamic-form",
  template: `
    <form [formGroup]="form" (submit)="onSubmit($event)">
      <app-error-form-summary
        [form]="form"
        [fields]="visibleFields"
      ></app-error-form-summary>
      <ng-container
        *ngFor="let field of visibleFields"
        dynamicField
        [field]="field"
        [group]="form"
      ></ng-container>
    </form>
  `,
  styles: [],
})
export class DynamicFormComponent implements OnChanges {
  @Input() fields: FieldConfig[] = [];
  @Input() groupValidators: Validators[];
  @Input() ignoreErrors;
  @Output() submit: EventEmitter<any> = new EventEmitter<any>();

  form: UntypedFormGroup;
  subs: Subscription[] = [];
  fieldVisibility: { [fieldName: string]: boolean } = {};

  get visibleFields(): FieldConfig[] {
    return this.fields.filter((field) => this.isFieldVisible(field));
  }

  get value() {
    return this.form.value;
  }

  constructor(private fb: UntypedFormBuilder, private userSvc: UserService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.fields) {
      this.subs.forEach((sub) => sub.unsubscribe());
      this.subs = [];
      this.fieldVisibility = {};
      this.fields = (changes.fields.currentValue as FieldConfig[]).filter(field => !field.hasOwnProperty('showIf') || field.showIf !== false);
      // Note: showFn fields are always kept in the list; visibility is evaluated each ngDoCheck
      this.form = this.createControl();
      this.refreshConditionalVisibility();
    }
  }

  ngDoCheck(): void {
    this.refreshConditionalVisibility();
  }

  onSubmit(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.ignoreErrors || this.form.valid) {
      this.submit.emit(this.form.value);
    } else {
      this.form.markAsTouched();
      (<EventEmitter<any>>this.form.statusChanges).emit(this.form.status);
    }
  }

  createControl() {
    const group = this.fb.group(
      {},
      { validators: this.groupValidators || [], updateOn: "submit" }
    );

    this.fields.forEach((field) => {
      if (field.type === "button") return;
      const control = this.fb.control(
        field.value,
        this.bindValidations(field.validations || [])
      );
      group.addControl(field.name, control);
    });

    this.setupConditionalVisibility(group);

    return group;
  }

  setupConditionalVisibility(group: UntypedFormGroup): void {
    this.fields.forEach((field) => {
      if (!field.name) return;
      // showFn and conditionalOn fields start visible; refreshConditionalVisibility
      // will evaluate them on the first ngDoCheck cycle.
      this.fieldVisibility[field.name] = true;
    });
  }

  refreshConditionalVisibility(): void {
    if (!this.form || !this.fields?.length) {
      return;
    }

    this.fields.forEach((field) => {
      if (!field.name) return;

      let isVisible: boolean;

      if (field.showFn) {
        isVisible = field.showFn();
      } else if (field.conditionalOn) {
        const parentControl = this.form.get(field.conditionalOn.field);
        const currentParentValue = this.getCurrentControlValue(parentControl);
        const matchesCondition = this.matchesConditionalValue(
          currentParentValue,
          field.conditionalOn.value
        );
        isVisible = field.conditionalOn.negate ? !matchesCondition : matchesCondition;
      } else {
        return;
      }

      const dependentControl = this.form.get(field.name);
      if (!dependentControl) return;

      if (this.fieldVisibility[field.name] !== isVisible) {
        this.fieldVisibility[field.name] = isVisible;
        if (isVisible && dependentControl.disabled) {
          dependentControl.enable({ emitEvent: false });
        }
        if (!isVisible && dependentControl.enabled) {
          dependentControl.disable({ emitEvent: false });
        }
      }
    });
  }

  getCurrentControlValue(control: any): any {
    if (control && control._pendingChange) {
      return control._pendingValue;
    }

    return control?.value;
  }

  matchesConditionalValue(currentValue: any, expectedValue: any): boolean {
    if (Array.isArray(expectedValue)) {
      if (Array.isArray(currentValue)) {
        return currentValue.some((item) => expectedValue.includes(item));
      }

      return expectedValue.includes(currentValue);
    }

    if (Array.isArray(currentValue)) {
      return currentValue.includes(expectedValue);
    }

    return currentValue === expectedValue;
  }

  isFieldVisible(field: FieldConfig): boolean {
    if (!field.name) return true;
    if (field.showFn) return this.fieldVisibility[field.name] !== false;
    if (!field.conditionalOn) return true;
    return this.fieldVisibility[field.name] !== false;
  }

  bindValidations(validations: any) {
    if (validations.length > 0) {
      const validList = [];
      validations.forEach((valid) => {
        validList.push(valid.validator);
      });
      return Validators.compose(validList);
    }
    return null;
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
